// Copyright (c) Ping. All rights reserved.
//
// Injects the message-bubble speech tail as a CSS `::after` pseudo-element
// on the bubble (web only), mirroring the Element/Matrix approach the user
// referenced (`.mx_EventTile_line:after` with `mask-image`). The tail is a
// box filled with the bubble color (via the `--ping-tail-color` custom
// property) and masked to the EXACT Element tail silhouettes:
//   bubble-tail-outgoing.svg (self, bottom-right)
//   bubble-tail-incoming.svg (other, bottom-left)
//
// Using a real `::after` (not a rendered child) means the tail never
// participates in layout/measurement and is the genuine Element treatment.
// The bubble element carries the classes + the color custom property.

// Exact Element tail path data (16×16 viewBox). Fill is irrelevant for a
// mask — only the shape's alpha is used; the color comes from the element's
// background-color (`--ping-tail-color`).
const OUTGOING_PATH =
  'M14.425 15.8468C15.4684 16.1262 16.2981 14.9051 15.712 13.9982C12.9639 9.74607 12.0971 6.26547 12.3411 1.27713C12.3935 0.204747 11.0767 -0.425383 10.3171 0.333801L0.332477 10.3133C-0.284376 10.9298 -0.00216967 11.9826 0.840447 12.2083L14.425 15.8468Z';
const INCOMING_PATH =
  'M1.48126 15.8468C0.437881 16.1262 -0.391895 14.9051 0.194239 13.9982C2.94237 9.74607 3.80915 6.26547 3.56517 1.27713C3.51272 0.204747 4.82956 -0.425383 5.58913 0.333801L15.5738 10.3133C16.1906 10.9298 15.9084 11.9826 15.0658 12.2083L1.48126 15.8468Z';

function maskUrl(path: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path d="${path}" fill="#000"/></svg>`;
  // encodeURIComponent handles the quotes/# safely inside url("…").
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

const STYLE_ELEMENT_ID = 'ping-bubble-tail-styles';

let injected = false;

/** Inject the tail `::after` rules once (web only). No-op on native /
 *  during SSR. */
export function injectBubbleTailStyles(): void {
  if (injected) return;
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ELEMENT_ID) !== null) {
    injected = true;
    return;
  }
  const out = maskUrl(OUTGOING_PATH);
  const inc = maskUrl(INCOMING_PATH);
  const css = `
.ping-bubble-tail::after {
  content: "";
  display: block;
  position: absolute;
  bottom: -2px;
  z-index: 9;
  pointer-events: none;
  background-color: var(--ping-tail-color, #537eff);
  -webkit-mask-position: center; mask-position: center;
  -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%; mask-size: 100% 100%;
}
.ping-bubble-tail-out::after {
  inset-inline-end: -2px; inset-inline-start: auto;
  width: 13px; height: 15px;
  -webkit-mask-image: ${out}; mask-image: ${out};
}
.ping-bubble-tail-in::after {
  inset-inline-start: -2px; inset-inline-end: auto;
  width: 14px; height: 17px;
  -webkit-mask-image: ${inc}; mask-image: ${inc};
}`;
  const el = document.createElement('style');
  el.id = STYLE_ELEMENT_ID;
  el.textContent = css;
  document.head.appendChild(el);
  injected = true;
}
